import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type {
  AnalysisDisplayConfig,
  District,
  PostDetailVisibility,
} from '@citizen-walk/shared-types';
import { POST_DETAIL_VISIBILITY_KEYS } from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { deleteField, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { upsertMaster, useAnalysisDisplayConfigs } from '../masters';

/** issue #24: 地区上書きトグルの日本語ラベル (テンプレ編集と同じ並び・文言)。 */
const AI_OUTPUT_VISIBILITY_LABELS: Record<keyof PostDetailVisibility, string> = {
  classificationAxes: '分類軸（カテゴリ/ハザード等の code+name 列）',
  assessment: '評価ラベル（ポジ/ネガ + レベル + 行スコア）',
  aggregateScores: '集約スコア（ハッと/ホッと・いいね/もったいない 等のサマリ）',
  fieldsTable: 'テーブル状の出力（fields[] 駆動の動的列テーブル）',
};

/**
 * AW2 (docs/108 Critical #4 / High): 地区別「分析ツール表示設定」紐付けパネル。
 *
 * old2 は地区詳細ページ内に DistrictDisplayConfigEditor を内包し、両軸ドロップダウン + bucketMethod 軸ルールを
 * 地区単位でインライン編集していた。v2 は AnalysisDisplayConfig を「地区横断の再利用マスター」に再設計したため
 * (docs/108 判断補正)、本 panel は old2 の運用フローを v2 設計に沿って復元する:
 *   - 既存の分析表示設定マスターを **ドロップダウン**で選択して紐付け (旧: free-text の ID 手打ち → 解消)
 *   - 「モード既定から地区専用の表示設定を作成」→ analysisDisplayConfigs/{districtId}-display を生成して紐付け
 *   - 「表示設定エディタで軸/bucketMethod を編集 →」master 編集画面 (AW1 で両軸/customRanges を form 化済) へ導線
 *   - 「地区専用設定を解除 (モード既定に戻す)」→ analysisDisplayConfigId を null に
 */

export interface DistrictAnalysisDisplayPanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictAnalysisDisplayPanel({
  district,
  uid,
  canEdit,
}: DistrictAnalysisDisplayPanelProps) {
  const { toast } = useToast();
  const configsState = useAnalysisDisplayConfigs();
  const [saving, setSaving] = useState(false);

  const configs = configsState.status === 'ready' ? configsState.items : [];
  // mode 一致を上位に並べる (old2 同様、地区 mode の設定を優先提示)。
  const sortedConfigs = useMemo(() => {
    return [...configs].sort((a, b) => {
      const am = a.mode === district.mode ? 0 : 1;
      const bm = b.mode === district.mode ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.displayName || a.id).localeCompare(b.displayName || b.id);
    });
  }, [configs, district.mode]);

  const currentId = district.analysisDisplayConfigId ?? '';
  const currentConfig = configs.find((c) => c.id === currentId);
  // 紐付け済 ID が一覧に無い (アーカイブ済 / 別データ) 場合の孤児表示用。
  const isOrphan = currentId !== '' && !currentConfig;

  async function linkConfig(id: string | null) {
    if (!canEdit) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', district.id),
        {
          analysisDisplayConfigId: id,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', id ? '表示設定を紐付けました' : '地区専用設定を解除しました');
    } catch (e) {
      toast('error', `保存に失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function createFromMode() {
    if (!canEdit) return;
    const newId = `${district.id}-display`;
    if (configs.some((c) => c.id === newId)) {
      // 既に存在する場合は紐付けのみ。
      await linkConfig(newId);
      return;
    }
    setSaving(true);
    try {
      await upsertMaster<AnalysisDisplayConfig>({
        collectionName: 'analysisDisplayConfigs',
        id: newId,
        uid,
        isNew: true,
        data: {
          mode: district.mode,
          displayName: `${district.name} 表示設定`,
          // 空の displayRules で生成。軸/bucketMethod は表示設定エディタ (AW1) で編集する。
          displayRules: {},
        } as Omit<
          AnalysisDisplayConfig,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
      await setDoc(
        doc(db, 'districts', district.id),
        {
          analysisDisplayConfigId: newId,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', `地区専用の表示設定 (${newId}) を作成して紐付けました`);
    } catch (e) {
      toast('error', `作成に失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  // issue #24: AI 出力フォーマット要素の投稿詳細表示可否の地区上書き (tri-state: 継承/表示/非表示)。
  const aiOverrides: PostDetailVisibility = district.aiOutputDisplayOverrides ?? {};
  function overrideValue(key: keyof PostDetailVisibility): 'inherit' | 'show' | 'hide' {
    const v = aiOverrides[key];
    return v === undefined ? 'inherit' : v ? 'show' : 'hide';
  }
  async function setAiOutputOverride(
    key: keyof PostDetailVisibility,
    value: 'inherit' | 'show' | 'hide',
  ) {
    if (!canEdit) return;
    const next: PostDetailVisibility = { ...aiOverrides };
    if (value === 'inherit') delete next[key];
    else next[key] = value === 'show';
    setSaving(true);
    try {
      // setDoc(merge:true) は map を deep-merge するため、「継承」に戻したキーを単に省くと旧値が残る。
      // 全 4 キーを明示し、show/hide=boolean、継承=deleteField() で確実に上書き/削除する。
      const hasAny = Object.keys(next).length > 0;
      const overridePayload: Record<string, unknown> = {};
      for (const k of POST_DETAIL_VISIBILITY_KEYS) {
        overridePayload[k] = k in next ? (next[k] as boolean) : deleteField();
      }
      await setDoc(
        doc(db, 'districts', district.id),
        {
          // 全キー継承に戻したらフィールドごと削除 (parse 側で undefined 扱い)。
          aiOutputDisplayOverrides: hasAny ? overridePayload : deleteField(),
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', '投稿詳細の表示上書きを保存しました');
    } catch (e) {
      toast('error', `保存に失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Card padding="lg" className="border-primary-200">
        <h3 className="text-base font-bold text-slate-900 mb-1">🔲 分析ツール表示設定の紐付け</h3>
        <p className="text-xs text-slate-600 mb-3">
          マトリクスの軸・bucketMethod・地図/ヒートマップの既定など、分析アプリの表示ルール (M5
          analysisDisplayConfig) を地区に紐付けます。未設定ならモード既定で表示されます。
        </p>

        <label className="grid grid-cols-1 gap-1 mb-2">
          <span className="text-xs text-slate-500">analysisDisplayConfigId (紐付ける表示設定)</span>
          <select
            value={currentId}
            onChange={(e) => void linkConfig(e.target.value || null)}
            disabled={!canEdit || saving || configsState.status !== 'ready'}
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
          >
            <option value="">(未設定 — モード既定で表示)</option>
            {sortedConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName || c.id} ({c.id})
                {c.mode === district.mode ? '' : ` ⚠ mode=${c.mode}`}
              </option>
            ))}
            {isOrphan && <option value={currentId}>⚠ {currentId} (一覧に無い設定を保持中)</option>}
          </select>
        </label>

        {configsState.status === 'loading' && (
          <p className="text-xs text-slate-400 mb-2">表示設定を読込中…</p>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Button
            type="button"
            size="sm"
            onClick={() => void createFromMode()}
            disabled={!canEdit || saving}
          >
            ＋ モード既定から地区専用の表示設定を作成
          </Button>
          {currentId && (
            <Link
              to="/masters/analysis-display"
              className="text-xs text-primary-600 hover:underline min-h-touch inline-flex items-center px-2"
            >
              表示設定エディタで軸 / bucketMethod を編集 →
            </Link>
          )}
          {currentId && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void linkConfig(null)}
              disabled={!canEdit || saving}
            >
              地区専用設定を解除 (モード既定に戻す)
            </Button>
          )}
        </div>
        {currentConfig && (
          <p className="text-[11px] text-slate-500 mt-2">
            紐付け中: <strong>{currentConfig.displayName || currentConfig.id}</strong> (mode=
            {currentConfig.mode})。軸の刻み方を変えるには上の「編集
            →」から表示設定エディタを開いてください。
          </p>
        )}
      </Card>

      {/* issue #24: 投稿詳細での AI 出力要素の表示可否 (地区上書き)。テンプレ既定は AI 出力フォーマット側。 */}
      <Card padding="lg" className="border-primary-200 mt-4">
        <h3 className="text-base font-bold text-slate-900 mb-1">
          🧩 投稿詳細での AI 出力の表示（地区上書き）
        </h3>
        <p className="text-xs text-slate-600 mb-3">
          投稿アプリの投稿詳細で、AI 出力フォーマットの各要素を表示するかを地区ごとに上書きします。
          「継承」は AI 出力フォーマット側のテンプレ既定に従います。テンプレ既定は
          <Link to="/masters/ai-output-formats" className="text-primary-600 hover:underline px-1">
            AI 出力フォーマット編集
          </Link>
          で設定できます。
        </p>
        <div className="grid grid-cols-1 gap-2">
          {POST_DETAIL_VISIBILITY_KEYS.map((k) => (
            <label key={k} className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-700">{AI_OUTPUT_VISIBILITY_LABELS[k]}</span>
              <select
                value={overrideValue(k)}
                onChange={(e) =>
                  void setAiOutputOverride(k, e.target.value as 'inherit' | 'show' | 'hide')
                }
                disabled={!canEdit || saving}
                className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
              >
                <option value="inherit">継承（テンプレ既定）</option>
                <option value="show">表示</option>
                <option value="hide">非表示</option>
              </select>
            </label>
          ))}
        </div>
      </Card>
    </>
  );
}
